<template>
  <div class="overlay-item" :style="rootStyle">
    <!-- lottie -->
    <div v-if="isLottie" ref="lottie" class="overlay-item__fill"></div>
    <!-- sticker (gif + 정적 이미지 png/webp/svg) -->
    <img v-else-if="isSticker" class="overlay-item__fill overlay-item__img" :src="stickerSrc" />
    <!-- text (외치기) -->
    <div v-else-if="isText" ref="text" class="overlay-item__text" :style="textStyle"></div>
    <!-- caption (자막) -->
    <div v-else-if="isCaption" class="overlay-item__captionwrap" :class="{ active: capActive, 'fade-out': capFade }">
      <!-- 이름표: 밴드 좌상단 북마크 탭 -->
      <div v-if="nameTag" class="overlay-item__nametag" :style="nameTagStyle">{{ nameTag }}</div>
      <div class="overlay-item__caption" :style="captionBoxStyle">
        <div ref="typewriter" class="overlay-item__tw" :style="captionTextStyle"></div>
      </div>
    </div>
  </div>
</template>

<script>
import lottieWeb from "lottie-web";

import Typewriter from "typewriter-effect/dist/core";
import { resolveResourceUrl } from "@/constants";
import { normalizeTransform, defaultDuration } from "@/constants/overlayDefaults";
import { nameTagPresetStyle } from "./nameTagPresets";
import { resolveTextFx, playTextFx } from "./textFxPresets";
import { buildCharSpans } from "./textSegments";

// 오버레이 단일 인스턴스. 위치(x,y=캔버스%)·크기(size)·레이어(behind/front)·지속(duration)을 감싸고
// overlayType(text|caption|lottie|sticker)에 맞는 콘텐츠를 렌더한다. 지속 만료 시 done emit → 부모가 제거.
// 기본값(위치/크기/지속)은 @/constants/overlayDefaults SSOT 공유(편집기 Gizmo·serialize와 동일).
// 기존 싱글턴 플레이어(TextAnime/LottiePlayer/GifPlayer/TextCaption)와 독립 (레거시 리액션은 그대로 싱글턴 경로).

export default {
  props: {
    overlay: { type: Object, required: true }, // { id, overlayType, transform:{x,y,size}, source, textSchemas, nameTag, style, duration }
    canvasPx: { type: Number, default: 400 },
    frozen: { type: Boolean, default: false }, // 편집기 정적 미리보기: 등장 애니·지속 만료 없이 그대로 표시
  },
  data() {
    return { capActive: false, capFade: false };
  },
  computed: {
    type() {
      return this.overlay.overlayType;
    },
    isLottie() {
      return this.type === "lottie";
    },
    isSticker() {
      return this.type === "sticker";
    },
    isText() {
      return this.type === "text";
    },
    isCaption() {
      return this.type === "caption";
    },
    // 포맷 정규화(SSOT): 박스형={x,y(좌상단),w,h}, 텍스트형={x,y(센터),size}. 레거시 센터+size도 정확 변환.
    tf() {
      return normalizeTransform(this.type, this.overlay.transform);
    },
    // 텍스트형 폰트 px (size = 높이 대비 %)
    fontPx() {
      return (this.tf.size / 100) * this.canvasPx;
    },
    // 외치기 박스 모드 여부 (w 있으면 박스+wrap, 없으면 레거시 센터+auto 폭)
    isTextBox() {
      return this.isText && this.tf.w != null;
    },
    rootStyle() {
      // 텍스트 박스 모드: 표시 영역 박스 + hAlign/vAlign 정렬 (기본 가운데/중앙, 폰트는 size로 독립)
      if (this.isTextBox) {
        const st = this.overlay.style || {};
        const H = { left: "flex-start", center: "center", right: "flex-end" };
        const V = { top: "flex-start", middle: "center", bottom: "flex-end" };
        return {
          position: "absolute",
          left: `${this.tf.x}%`,
          top: `${this.tf.y}%`,
          width: `${(this.tf.w / 100) * this.canvasPx}px`,
          height: `${(this.tf.h / 100) * this.canvasPx}px`,
          display: "flex",
          alignItems: V[st.vAlign] || "center",
          justifyContent: H[st.hAlign] || "center",
          pointerEvents: "none",
        };
      }
      // 박스형: 좌상단 앵커 + 자유 비율 w/h
      if (this.isLottie || this.isSticker) {
        return {
          position: "absolute",
          left: `${this.tf.x}%`,
          top: `${this.tf.y}%`,
          width: `${(this.tf.w / 100) * this.canvasPx}px`,
          height: `${(this.tf.h / 100) * this.canvasPx}px`,
          pointerEvents: "none",
        };
      }
      // 자막: 배경 밴드 = 1급 박스 (좌상단 앵커 + 자유 비율) — 텍스트는 hAlign/vAlign으로 내부 배치
      if (this.isCaption) {
        return {
          position: "absolute",
          left: `${this.tf.x}%`,
          top: `${this.tf.y}%`,
          width: `${(this.tf.w / 100) * this.canvasPx}px`,
          height: `${(this.tf.h / 100) * this.canvasPx}px`,
          pointerEvents: "none",
        };
      }
      // 텍스트(외치기): 센터 앵커 (박스가 내용 따라 자동 폭)
      return {
        position: "absolute",
        left: `${this.tf.x}%`,
        top: `${this.tf.y}%`,
        transform: "translate(-50%, -50%)",
        pointerEvents: "none",
      };
    },
    // 자막 밴드: 박스 채움 + 정렬 (hAlign→justify/textAlign, vAlign→align — column flex)
    captionBoxStyle() {
      const st = this.overlay.style || {};
      const JUSTIFY = { top: "flex-start", middle: "center", bottom: "flex-end" };
      const ALIGN = { left: "flex-start", center: "center", right: "flex-end" };
      return {
        justifyContent: JUSTIFY[st.vAlign] || "flex-start",
        alignItems: ALIGN[st.hAlign] || "flex-start",
        textAlign: st.hAlign || "left",
      };
    },
    textStyle() {
      const st = this.overlay.style || {};
      return {
        fontSize: `${this.fontPx}px`,
        // color/stroke는 글자 span(세그먼트)이 담당 — 여기선 지정하지 않음
        fontFamily: "Ownglyph_meetme-Rg",
        textAlign: (this.isTextBox && st.hAlign) || "center",
        // 박스 모드: 영역 안에서 자동 줄바꿈(wrap). 레거시(auto 폭): \n만 줄바꿈
        whiteSpace: this.isTextBox ? "pre-wrap" : "pre",
        ...(this.isTextBox ? { maxWidth: "100%", overflowWrap: "break-word" } : {}),
        lineHeight: "1.2",
        fontWeight: "900",
      };
    },
    captionTextStyle() {
      const st = this.overlay.style || {};
      return { fontSize: `${this.fontPx}px`, color: st.color || "white" };
    },
    stickerSrc() {
      return resolveResourceUrl(this.overlay.source, "gifs");
    },
    nameTag() {
      const nt = this.overlay.nameTag;
      return (nt && (nt.text || nt.template)) || ""; // 파싱 전(정적 미리보기)엔 template 원문
    },
    nameTagStyle() {
      const preset = this.overlay.style && this.overlay.style.nameTagPreset;
      return { fontSize: `${this.fontPx * 0.8}px`, ...nameTagPresetStyle(preset) };
    },
    resolvedDuration() {
      const d = this.overlay.duration;
      return d && d > 0 ? d : defaultDuration(this.type);
    },
  },
  watch: {
    // 편집기 정적 미리보기: 텍스트 문자열은 명령형(textContent)이라 편집 시 반응형으로 갱신
    "overlay.textSchemas": {
      deep: true,
      handler() {
        if (!this.frozen) return;
        this.$nextTick(() => this._syncFrozenText());
      },
    },
  },
  mounted() {
    this._timers = [];
    if (this.isLottie) this._playLottie();
    else if (this.isText) this._playText();
    else if (this.isCaption) this._playCaption();
    // sticker: <img>가 바로 표시됨

    // 정적 미리보기는 지속 만료(자동 제거) 없음
    if (!this.frozen) {
      this._timers.push(setTimeout(() => this.$emit("done", this.overlay.id), this.resolvedDuration));
    }
  },
  beforeDestroy() {
    (this._timers || []).forEach(clearTimeout);
    if (this._textFx) this._textFx.cancel();
    if (this._lottie) {
      try {
        this._lottie.destroy();
      } catch (e) {
        /* noop */
      }
    }
  },
  methods: {
    // 정적 미리보기에서 텍스트/자막 문자열 갱신 (편집 반영)
    _syncFrozenText() {
      const schemas = this.overlay.textSchemas || [];
      if (this.isText && this.$refs.text) {
        const st = this.overlay.style || {};
        buildCharSpans(this.$refs.text, schemas, { defaultColor: st.color || "white", defaultStroke: st.stroke || "black" });
      } else if (this.isCaption && this.$refs.typewriter) {
        this.$refs.typewriter.textContent = (schemas[0] && schemas[0].text) || "";
      }
    },
    _playLottie() {
      const path = resolveResourceUrl(this.overlay.source, "lotties/background");
      if (!path) return;
      this._lottie = lottieWeb.loadAnimation({
        container: this.$refs.lottie,
        renderer: "svg",
        loop: this.overlay.style && this.overlay.style.loop === false ? false : true,
        autoplay: true,
        path,
      });
    },
    _playText() {
      const el = this.$refs.text;
      if (!el) return;
      // 세그먼트(글자별 색/보더) → 글자 span 빌드. 기본색은 style.color/stroke(전체 기본값)
      const st = this.overlay.style || {};
      const chars = buildCharSpans(el, this.overlay.textSchemas, { defaultColor: st.color || "white", defaultStroke: st.stroke || "black" });
      if (this.frozen) {
        el.style.opacity = "1";
        return;
      }
      // 등장/지속/퇴장 3슬롯 효과 (style.textFx — 레거시 anim은 resolveTextFx가 매핑)
      const fx = resolveTextFx(this.overlay.style);
      this._textFx = playTextFx(el, fx, this.resolvedDuration, chars);
    },
    _playCaption() {
      this.capActive = true;
      const schemas = this.overlay.textSchemas || [];
      const text = (schemas[0] && schemas[0].text) || "";
      if (this.frozen) {
        // 정적: 타이핑·페이드 없이 전체 텍스트 즉시 표시
        if (this.$refs.typewriter) this.$refs.typewriter.textContent = text;
        return;
      }
      const tw = new Typewriter(this.$refs.typewriter, { loop: false, autoStart: false, cursor: "", delay: 30 });
      // typeString은 HTML 해석 — 사용자 텍스트는 이스케이프하고 \n만 <br/>로 (줄바꿈 반영 + 마크업 주입 방지)
      const html = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br/>");
      tw.typeString(html).start();
      // duration 끝나기 직전 페이드아웃
      const fadeAt = Math.max(0, this.resolvedDuration - 500);
      this._timers.push(
        setTimeout(() => {
          this.capFade = true;
        }, fadeAt)
      );
    },
  },
};
</script>

<style scoped>
.overlay-item__fill {
  width: 100%;
  height: 100%;
}
.overlay-item__img {
  object-fit: contain;
}
.overlay-item__text {
  display: inline-block;
}
.overlay-item__captionwrap {
  position: relative;
  width: 100%;
  height: 100%;
  display: none;
}
.overlay-item__captionwrap.active {
  display: block;
  animation: overlayFadeIn 0.6s;
}
.overlay-item__captionwrap.fade-out {
  animation: overlayFadeOut 0.5s forwards;
}
.overlay-item__caption {
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column; /* vAlign=justify, hAlign=align */
  overflow: hidden;
}
@keyframes overlayFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
@keyframes overlayFadeOut {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}
.overlay-item__nametag {
  position: absolute;
  bottom: 100%; /* 밴드 위에 붙는 북마크 탭 */
  left: 0;
  display: flex;
  align-items: center;
  white-space: nowrap;
  /* 배경/색/패딩/라운드는 프리셋(nameTagPresets) 인라인 스타일 */
}
.overlay-item__tw {
  /* 기본 여백 최소화 (top 정렬 시 밴드에 붙게) — 이름표는 밴드 밖(위) 탭이라 영향 없음 */
  padding: 4px 6px;
  line-height: 1.3;
  color: white;
  white-space: pre-line; /* \n 줄바꿈 반영 (frozen textContent 경로), 길면 자동 줄바꿈 허용 */
}
</style>
